import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Check, X, Trash2, RefreshCw, FileDown, Upload, History, Wallet, ClipboardList, PlusCircle, MinusCircle, Paperclip } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const fmtDate = (d) => {
  if (!d) return '-';
  const parts = d.split('T')[0].split('-');
  if (parts.length !== 3) return d;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const fmtDateTime = (d) => {
  if (!d) return '-';
  try {
    const dt = new Date(d);
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yy = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, '0');
    const mi = String(dt.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
  } catch { return d; }
};

export default function LeavesPage() {
  const { user, isRole } = useAuth();
  const canManage = isRole('admin', 'aspp', 'qhse', 'capo_cantiere');
  const isAdmin = isRole('admin');

  const [tab, setTab] = useState('richieste');
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [balances, setBalances] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('');
  const [historyEmpFilter, setHistoryEmpFilter] = useState('');
  const [historyCatFilter, setHistoryCatFilter] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [showAccrue, setShowAccrue] = useState(false);

  const [form, setForm] = useState({ request_type: 'ferie', start_date: '', end_date: '', reason: '', employee_id: '', attachment_url: '' });
  const [adjustForm, setAdjustForm] = useState({ employee_id: '', category: 'ferie', hours: '', description: '' });
  const [accrueForm, setAccrueForm] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [uploading, setUploading] = useState(false);
  const attachInputRef = useRef(null);
  const [attachLeaveId, setAttachLeaveId] = useState(null);

  const currentYear = new Date().getFullYear();

  const loadRequests = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/leaves', { params });
      setLeaves(res.data);
    } catch (err) { console.error(err); }
  }, [statusFilter]);

  const loadEmployees = useCallback(async () => {
    if (!canManage) return;
    try {
      const res = await api.get('/employees');
      setEmployees(res.data.filter(e => e.is_active));
    } catch (err) { console.error(err); }
  }, [canManage]);

  const loadBalances = useCallback(async () => {
    try {
      const res = await api.get('/leave-balances', { params: { year: currentYear } });
      setBalances(res.data);
    } catch (err) { console.error(err); }
  }, [currentYear]);

  const loadHistory = useCallback(async () => {
    try {
      const params = { year: currentYear };
      if (historyEmpFilter) params.employee_id = historyEmpFilter;
      if (historyCatFilter && historyCatFilter !== 'all') params.category = historyCatFilter;
      const res = await api.get('/leave-history', { params });
      setHistory(res.data);
    } catch (err) { console.error(err); }
  }, [currentYear, historyEmpFilter, historyCatFilter]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadRequests(), loadEmployees()]);
      setLoading(false);
    };
    init();
  }, [loadRequests, loadEmployees]);

  useEffect(() => { if (tab === 'saldi') loadBalances(); }, [tab, loadBalances]);
  useEffect(() => { if (tab === 'storico') loadHistory(); }, [tab, loadHistory]);

  const handleCreate = async () => {
    try {
      const payload = { ...form };
      if (canManage && form.employee_id) {
        await api.post('/leaves', payload);
      } else if (user?.employee_id) {
        payload.employee_id = user.employee_id;
        await api.post('/leaves', payload);
      } else if (canManage && !form.employee_id) {
        alert('Seleziona un dipendente');
        return;
      } else {
        alert("Il tuo account non e' collegato a un dipendente.");
        return;
      }
      setShowCreate(false);
      setForm({ request_type: 'ferie', start_date: '', end_date: '', reason: '', employee_id: '', attachment_url: '' });
      loadRequests();
      if (tab === 'saldi') loadBalances();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleUploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, attachment_url: res.data.file_url }));
    } catch (err) {
      alert('Errore upload file');
    } finally {
      setUploading(false);
    }
  };

  const triggerAttachToExisting = (leaveId) => {
    setAttachLeaveId(leaveId);
    attachInputRef.current?.click();
  };

  const handleAttachToExisting = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !attachLeaveId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.put(`/leaves/${attachLeaveId}`, { attachment_url: res.data.file_url });
      loadRequests();
    } catch (err) {
      alert('Errore upload allegato');
    } finally {
      setUploading(false);
      setAttachLeaveId(null);
      if (attachInputRef.current) attachInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa richiesta?')) return;
    try {
      await api.delete(`/leaves/${id}`);
      loadRequests();
      if (tab === 'saldi') loadBalances();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/leaves/${id}/approve`, { notes: '' });
      loadRequests();
      if (tab === 'saldi') loadBalances();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleReject = async (id) => {
    const notes = prompt('Motivo del rifiuto:');
    if (notes === null) return;
    try {
      await api.put(`/leaves/${id}/reject`, { notes });
      loadRequests();
      if (tab === 'saldi') loadBalances();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleAccrue = async () => {
    try {
      await api.post('/leave-balances/accrue', accrueForm);
      setShowAccrue(false);
      loadBalances();
      loadHistory();
      alert('Maturazione completata!');
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleAdjust = async () => {
    if (!adjustForm.employee_id || !adjustForm.hours) {
      alert('Compila tutti i campi obbligatori');
      return;
    }
    try {
      await api.post('/leave-balances/adjust', {
        ...adjustForm,
        hours: parseFloat(adjustForm.hours),
        year: currentYear,
      });
      setShowAdjust(false);
      setAdjustForm({ employee_id: '', category: 'ferie', hours: '', description: '' });
      loadBalances();
      loadHistory();
      alert('Rettifica applicata!');
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const statusBadge = (status) => {
    const m = {
      in_attesa: { l: 'In Attesa', c: 'bg-amber-50 text-amber-700 border-amber-200' },
      approvata: { l: 'Approvata', c: 'bg-green-50 text-green-700 border-green-200' },
      rifiutata: { l: 'Rifiutata', c: 'bg-red-50 text-red-700 border-red-200' },
    };
    const s = m[status] || m.in_attesa;
    return <Badge variant="outline" className={s.c}>{s.l}</Badge>;
  };

  const typeLabel = (t) => {
    const m = { ferie: 'Ferie', permesso: 'Permesso', malattia: 'Malattia', assenza: 'Assenza' };
    return m[t] || t;
  };

  const movementLabel = (t) => {
    const m = { maturazione: 'Maturazione', godimento: 'Godimento', rettifica_positiva: 'Rettifica (+)', rettifica_negativa: 'Rettifica (-)' };
    return m[t] || t;
  };

  const movementBadge = (t) => {
    const colors = {
      maturazione: 'bg-blue-50 text-blue-700 border-blue-200',
      godimento: 'bg-orange-50 text-orange-700 border-orange-200',
      rettifica_positiva: 'bg-green-50 text-green-700 border-green-200',
      rettifica_negativa: 'bg-red-50 text-red-700 border-red-200',
    };
    return <Badge variant="outline" className={colors[t] || 'bg-slate-50 text-slate-700 border-slate-200'}>{movementLabel(t)}</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-slate-400">Caricamento...</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leaves-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Ferie, Permessi e Malattia</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="richieste" data-testid="tab-richieste" className="gap-1.5">
            <ClipboardList size={14} /> Richieste
          </TabsTrigger>
          <TabsTrigger value="saldi" data-testid="tab-saldi" className="gap-1.5">
            <Wallet size={14} /> Saldi
          </TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico" className="gap-1.5">
            <History size={14} /> Storico
          </TabsTrigger>
        </TabsList>

        {/* ── TAB RICHIESTE ── */}
        <TabsContent value="richieste" className="mt-4">
          <div className="flex items-center gap-3 mb-4">
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); }}>
              <SelectTrigger className="w-40" data-testid="leave-filter">
                <SelectValue placeholder="Tutti gli stati" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="in_attesa">In Attesa</SelectItem>
                <SelectItem value="approvata">Approvate</SelectItem>
                <SelectItem value="rifiutata">Rifiutate</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1" />
            <Button onClick={() => setShowCreate(true)} data-testid="add-leave-btn">
              <Plus size={16} className="mr-1" /> Nuova Richiesta
            </Button>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {canManage && <TableHead>Dipendente</TableHead>}
                    <TableHead>Tipo</TableHead>
                    <TableHead>Dal</TableHead>
                    <TableHead>Al</TableHead>
                    <TableHead className="hidden sm:table-cell">Ore</TableHead>
                    <TableHead className="hidden md:table-cell">Motivo</TableHead>
                    <TableHead>Stato</TableHead>
                    {canManage && <TableHead>Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaves.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Nessuna richiesta</TableCell></TableRow>
                  ) : leaves.map(l => (
                    <TableRow key={l.id} className="hover:bg-slate-50">
                      {canManage && (
                        <TableCell className="text-sm font-medium">
                          {l.employee_name}
                          {l.created_by_name && l.created_by_name !== l.employee_name && <div className="text-[11px] font-normal text-slate-400" data-testid={`inserita-da-${l.id}`}>inserita da {l.created_by_name}</div>}
                          {l.approved_by_name && l.status !== 'in_attesa' && l.approved_by_name !== l.employee_name && <div className="text-[11px] font-normal text-slate-400">{l.status === 'rifiutata' ? 'rifiutata' : 'approvata'} da {l.approved_by_name}</div>}
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge variant="outline" className={
                          l.request_type === 'malattia' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                          'bg-slate-100 text-slate-700 border-slate-200'
                        }>{typeLabel(l.request_type)}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{fmtDate(l.start_date)}</TableCell>
                      <TableCell className="text-sm">{fmtDate(l.end_date)}</TableCell>
                      <TableCell className="hidden sm:table-cell text-sm">{l.hours || '-'}</TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-slate-600 max-w-[200px] truncate">
                        {l.reason || '-'}
                        {l.attachment_url && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${l.attachment_url}`} target="_blank" rel="noreferrer" className="ml-2 text-blue-600 hover:underline text-xs">
                            [allegato]
                          </a>
                        )}
                        {l.request_type === 'malattia' && !l.attachment_url && (
                          <Button size="sm" variant="ghost" onClick={() => triggerAttachToExisting(l.id)} className="ml-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-auto p-0.5" data-testid={`attach-cert-${l.id}`} disabled={uploading}>
                            <Paperclip size={13} className="mr-0.5" /><span className="text-xs">Allega cert.</span>
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>{statusBadge(l.status)}</TableCell>
                      {canManage && (
                        <TableCell>
                          <div className="flex gap-1">
                            {l.status === 'in_attesa' && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => handleApprove(l.id)} className="text-green-600 hover:text-green-700 hover:bg-green-50" data-testid={`approve-${l.id}`}>
                                  <Check size={16} />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleReject(l.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`reject-${l.id}`}>
                                  <X size={16} />
                                </Button>
                              </>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(l.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-${l.id}`}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TAB SALDI ── */}
        <TabsContent value="saldi" className="mt-4">
          {isAdmin && (
            <div className="flex items-center gap-3 mb-4">
              <Button variant="outline" onClick={() => setShowAccrue(true)} data-testid="accrue-btn">
                <RefreshCw size={14} className="mr-1" /> Esegui Maturazione
              </Button>
              <Button variant="outline" onClick={() => setShowAdjust(true)} data-testid="adjust-btn">
                <PlusCircle size={14} className="mr-1" /> Rettifica Manuale
              </Button>
            </div>
          )}

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dipendente</TableHead>
                    <TableHead className="text-center" colSpan={3}>
                      <span className="text-blue-700">Ferie (ore)</span>
                    </TableHead>
                    <TableHead className="text-center" colSpan={3}>
                      <span className="text-indigo-700">Permessi ROL (ore)</span>
                    </TableHead>
                  </TableRow>
                  <TableRow>
                    <TableHead></TableHead>
                    <TableHead className="text-center text-xs text-blue-600">Maturate</TableHead>
                    <TableHead className="text-center text-xs text-blue-600">Godute</TableHead>
                    <TableHead className="text-center text-xs text-blue-600 font-bold">Residue</TableHead>
                    <TableHead className="text-center text-xs text-indigo-600">Maturati</TableHead>
                    <TableHead className="text-center text-xs text-indigo-600">Goduti</TableHead>
                    <TableHead className="text-center text-xs text-indigo-600 font-bold">Residui</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {balances.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Nessun saldo disponibile</TableCell></TableRow>
                  ) : balances.map(b => (
                    <TableRow key={b.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium text-sm">{b.employee_name}</TableCell>
                      <TableCell className="text-center text-sm">{b.ferie_maturate}</TableCell>
                      <TableCell className="text-center text-sm">{b.ferie_godute}</TableCell>
                      <TableCell className="text-center text-sm font-bold">
                        <span className={b.ferie_residue < 0 ? 'text-red-600' : b.ferie_residue > 0 ? 'text-green-600' : ''}>
                          {b.ferie_residue}
                        </span>
                      </TableCell>
                      <TableCell className="text-center text-sm">{b.permessi_maturati}</TableCell>
                      <TableCell className="text-center text-sm">{b.permessi_goduti}</TableCell>
                      <TableCell className="text-center text-sm font-bold">
                        <span className={b.permessi_residui < 0 ? 'text-red-600' : b.permessi_residui > 0 ? 'text-green-600' : ''}>
                          {b.permessi_residui}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TAB STORICO ── */}
        <TabsContent value="storico" className="mt-4">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {canManage && (
              <Select value={historyEmpFilter} onValueChange={setHistoryEmpFilter}>
                <SelectTrigger className="w-52" data-testid="history-emp-filter">
                  <SelectValue placeholder="Tutti i dipendenti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={historyCatFilter} onValueChange={setHistoryCatFilter}>
              <SelectTrigger className="w-40" data-testid="history-cat-filter">
                <SelectValue placeholder="Tutte le cat." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                <SelectItem value="ferie">Ferie</SelectItem>
                <SelectItem value="permesso">Permessi</SelectItem>
                <SelectItem value="malattia">Malattia</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {canManage && <TableHead>Dipendente</TableHead>}
                    <TableHead>Data</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Tipo Mov.</TableHead>
                    <TableHead className="text-center">Ore</TableHead>
                    <TableHead className="hidden sm:table-cell">Descrizione</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Nessun movimento</TableCell></TableRow>
                  ) : history.map(h => (
                    <TableRow key={h.id} className="hover:bg-slate-50">
                      {canManage && <TableCell className="text-sm font-medium">{h.employee_name}</TableCell>}
                      <TableCell className="text-sm">{fmtDateTime(h.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          h.category === 'ferie' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          h.category === 'permesso' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                          'bg-purple-50 text-purple-700 border-purple-200'
                        }>{typeLabel(h.category)}</Badge>
                      </TableCell>
                      <TableCell>{movementBadge(h.movement_type)}</TableCell>
                      <TableCell className="text-center text-sm font-medium">
                        <span className={
                          h.movement_type === 'godimento' || h.movement_type === 'rettifica_negativa'
                            ? 'text-red-600' : 'text-green-600'
                        }>
                          {h.movement_type === 'godimento' || h.movement_type === 'rettifica_negativa' ? '-' : '+'}{h.hours}
                        </span>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-slate-600 max-w-[300px] truncate">{h.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Hidden file input for attaching to existing malattia requests */}
      <input
        type="file"
        ref={attachInputRef}
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={handleAttachToExisting}
        data-testid="attach-existing-input"
      />

      {/* ── DIALOG: Nuova Richiesta ── */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuova Richiesta</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {canManage && (
              <div className="space-y-1.5">
                <Label>Dipendente *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="leave-employee-select"><SelectValue placeholder="Seleziona dipendente..." /></SelectTrigger>
                  <SelectContent>
                    {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Tipo *</Label>
              <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ferie">Ferie</SelectItem>
                  <SelectItem value="permesso">Permesso (ROL + Ex Fest.)</SelectItem>
                  <SelectItem value="malattia">Malattia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Dal *</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start" /></div>
              <div className="space-y-1.5"><Label>Al *</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end" /></div>
            </div>
            <div className="space-y-1.5"><Label>Motivo</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Opzionale..." /></div>

            {form.request_type === 'malattia' && (
              <div className="space-y-1.5">
                <Label>Allegato (certificato medico)</Label>
                <div className="flex items-center gap-2">
                  <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleUploadAttachment} disabled={uploading} data-testid="leave-attachment" />
                  {uploading && <span className="text-xs text-slate-500">Caricamento...</span>}
                  {form.attachment_url && <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Caricato</Badge>}
                </div>
              </div>
            )}

            {form.request_type === 'malattia' && (
              <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                La malattia non scala i saldi di ferie/permessi. Verranno tracciate solo nello storico.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={!form.start_date || !form.end_date || (canManage && !form.employee_id && !user?.employee_id)} data-testid="leave-save-btn">Invia Richiesta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG: Maturazione ── */}
      <Dialog open={showAccrue} onOpenChange={setShowAccrue}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Esegui Maturazione Mensile</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Accredita automaticamente ferie (13,33 ore) e permessi ROL+Ex Fest. (7,33 ore) a tutti i dipendenti attivi per il mese selezionato.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Mese</Label>
                <Select value={String(accrueForm.month)} onValueChange={v => setAccrueForm(prev => ({ ...prev, month: parseInt(v) }))}>
                  <SelectTrigger data-testid="accrue-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => <SelectItem key={i + 1} value={String(i + 1)}>{['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'][i]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Anno</Label>
                <Input type="number" value={accrueForm.year} onChange={e => setAccrueForm(prev => ({ ...prev, year: parseInt(e.target.value) }))} data-testid="accrue-year" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccrue(false)}>Annulla</Button>
            <Button onClick={handleAccrue} data-testid="accrue-confirm-btn">Esegui Maturazione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG: Rettifica Manuale ── */}
      <Dialog open={showAdjust} onOpenChange={setShowAdjust}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Rettifica Manuale Saldo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Dipendente *</Label>
              <Select value={adjustForm.employee_id} onValueChange={v => setAdjustForm(prev => ({ ...prev, employee_id: v }))}>
                <SelectTrigger data-testid="adjust-employee"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Categoria *</Label>
              <Select value={adjustForm.category} onValueChange={v => setAdjustForm(prev => ({ ...prev, category: v }))}>
                <SelectTrigger data-testid="adjust-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ferie">Ferie</SelectItem>
                  <SelectItem value="permesso">Permessi (ROL + Ex Fest.)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Ore (+ aggiunge, - sottrae) *</Label>
              <Input type="number" step="0.5" value={adjustForm.hours} onChange={e => setAdjustForm(prev => ({ ...prev, hours: e.target.value }))} placeholder="es. 8 oppure -4" data-testid="adjust-hours" />
            </div>
            <div className="space-y-1.5">
              <Label>Motivazione</Label>
              <Textarea value={adjustForm.description} onChange={e => setAdjustForm(prev => ({ ...prev, description: e.target.value }))} placeholder="es. Riporto anno precedente" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjust(false)}>Annulla</Button>
            <Button onClick={handleAdjust} data-testid="adjust-confirm-btn">Applica Rettifica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
